/* eslint-disable parog-web/migrate-non-patch-forms */
import type ModelRegistry from "@ember-data/types/registries/model";
import { action } from "@ember/object";
import { service } from "@ember/service";
import JournalEventBasedComponent from "parog-web/components/forc/abstract/journal-event-based-component";
import type BioExamPrescribedJournalEvent from "parog-web/models/bio-exam-prescribed-journal-event";
import type BioExamResultsReceivedJournalEvent from "parog-web/models/bio-exam-results-received-journal-event";
import type ConsultationReportWrittenJournalEvent from "parog-web/models/consultation-report-written-journal-event";
import type DpCycleDeclaredJournalEvent from "parog-web/models/dp-cycle-declared-journal-event";
import type File from "parog-web/models/file";
import type FileUploadedJournalEvent from "parog-web/models/file-uploaded-journal-event";
import type MedicalEventJournalEvent from "parog-web/models/medical-event-journal-event";
import type MedicalImagingPrescribedJournalEvent from "parog-web/models/medical-imaging-prescribed-journal-event";
import type MedicationPrescribedJournalEvent from "parog-web/models/medication-prescribed-journal-event";
import type ParamedicalCarePrescribedJournalEvent from "parog-web/models/paramedical-care-prescribed-journal-event";
import type Patient from "parog-web/models/patient";
import type ParogService from "parog-web/services/parog";
import emberSwal from "ui-kit/utils/ember-swal";
import type { ForcBaseArgs } from "./base-component";

export interface Args extends ForcBaseArgs {
  patient: Patient;
}

export default class CrudJournalEventBaseComponent<
  T = unknown,
  Args extends ForcBaseArgs = ForcBaseArgs
> extends JournalEventBasedComponent<Args> {
  @service declare parog: ParogService;

  /**
   * Use
   * <WrForm @data={{this.typedChangeset}}>
   * instead of
   * <WrForm @data={{this.changeset}}>
   * to get full typing support in your form
   *
   * See https://github.com/CrowdStrike/ember-headless-form/issues/211
   */
  get typedChangeset(): T {
    return this.changeset as unknown as T;
  }

  get model(): typeof this.journalEventClass {
    if (this.isNewMode) {
      return this.store.createRecord(this.journalEventClassType, {
        occurredAt: new Date(),
        ...this.initialModel
      });
    }

    return this.retrievedJournalEvent as typeof this.journalEventClass;
  }

  get initialModel(): Record<string, any> {
    return {};
  }

  async beforeSave(): Promise<void> {
    return;
  }

  @action
  async saveWithFiles(
    journalEventClassType: keyof ModelRegistry = this.journalEventClassType
  ): Promise<
    | BioExamResultsReceivedJournalEvent
    | ConsultationReportWrittenJournalEvent
    | FileUploadedJournalEvent
    | BioExamPrescribedJournalEvent
    | MedicationPrescribedJournalEvent
    | MedicalImagingPrescribedJournalEvent
    | ParamedicalCarePrescribedJournalEvent
    | MedicalEventJournalEvent
    | DpCycleDeclaredJournalEvent
    | undefined
  > {
    if (!this.isDirty) {
      return;
    }

    this.changeset.set("patient", this.args.patient);

    await this.beforeSave();

    await this.changeset.validate();
    const journalEventPayload = (
      this.changeset.execute().data as typeof this.journalEventClass
    ).serialize().data;
    journalEventPayload["type"] = `${journalEventClassType}s`;
    this.changeset.rollback();

    const body = JSON.stringify({
      data: {
        "journal-event": {
          data: journalEventPayload
        },
        "file-attachments": (this.changeset.get("files") as File[]).map(file =>
          file.fileAttachment.serialize()
        ),
        "file-contents": (this.changeset.get("files") as File[]).map(file =>
          file.fileContent.serialize()
        )
      }
    });

    let journalEvent;

    const { response, content } = await this.requestManager.request({
      url: "/api/v1/journal_event_with_attachment",
      method: "POST",
      body: body
    });

    if (!response.ok) {
      emberSwal.fire({
        title: "Oups, échec de l'importation de votre document.",
        text: "Veuillez réessayer ou contacter le support Sêmeia si le problème persiste.",
        icon: "error"
      });
    } else {
      // Fetch saved record
      journalEvent = await this.store.findRecord(
        content.data.type.slice(0, -1),
        content.data.id,
        {
          include: "file-attachments"
        }
      );
    }

    return journalEvent;
  }

  @action
  async save(): Promise<typeof this.journalEventClass> {
    if (this.changeset.get("files")) {
      return (await this.saveWithFiles()) as typeof this.journalEventClass;
    }

    if (!this.isDirty) {
      return;
    }

    /**
     * Set the patient before saving the changeset, if the record
     * is new.
     * As it is done in the `computePayload` method.
     */
    if (this.isNewMode) {
      this.changeset.set("patient", this.args.patient);
    }

    await this.changeset.execute();
    await this.beforeSave();
    await this.changeset.save();
    await this.draft.onDidSave();

    /**
     * Unlike Forc::Provdider, WrForm is not responsible of any logic after submitting the form
     * under Wiser, this logic now belongs to this save method
     */
    if (this.parog.wiser && this.args.afterCompleted)
      this.args.afterCompleted();

    super.popToast();

    return this.changeset.data;
  }
}
